import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import axios from 'axios'
import { Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Fab, IconButton, Snackbar, Tab, Tabs, TextField } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import LaunchIcon from '@material-ui/icons/Launch'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import MyLocationIcon from '@material-ui/icons/MyLocation'
import TrackChangesIcon from '@material-ui/icons/TrackChanges'
import BorderOuterIcon from '@material-ui/icons/BorderOuter'
import LayersIcon from '@material-ui/icons/Layers'
import { PropertyEditForm } from './PropertyEditModal'
import PropertyReviewMap from './PropertyReviewMap'
import PropertyReviewStatusCard from './PropertyReviewStatusCard'
import { V4_T, getPropertyRoute } from './propertyDetailHelpers'
import { TRIAGE_FILTERS, buildReviewQueue } from './propertyReviewQueue'
import { FLAG_CATEGORIES, flagCategoryLabel, buildOpenFlagMap } from './propertyReviewFlags'

// Tab -> PropertyEditForm section keys, for the slide-over panel's visibleSections slicing.
const TAB_SECTIONS = {
  prescription: ['prescription', 'frequencies', 'mowing', 'irrigation'],
  schedule: ['routeLock', 'serviceDays', 'accessHours'],
  checklist: ['checklist'],
  billing: ['billing']
}
const SERIF = "'Iowan Old Style', 'Palatino Linotype', Georgia, serif"

// All map layers on — the per-open default for the FAB toggles.
const ALL_LAYERS_ON = { geofence: true, parcels: true, takeoffs: true }

const formatShortDate = (value) => {
  if (!value) return '—'
  const date = new Date(value)
  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

// Fullscreen triage walkthrough over the FULL property list: edit-form sidebar
// + read-only site map. The modal owns its queue (search + broken-data chips,
// OR semantics) and pins the current property by id — filtering never yanks
// the user off the property they're editing. Top-level layout uses inline
// styles per this directory's convention (PropertyEditModal.js).
function PropertyReviewModal ({ open, onClose, properties, url, crews, propertyOptions, onSave, initialPropertyId = null }) {
  const [dirty, setDirty] = useState(false)
  // Remount key for the sidebar form — Reset button only; navigation re-seeds
  // via the `property` prop change, keeping the form mounted so its route
  // fetch runs once per modal open.
  const [resetNonce, setResetNonce] = useState(0)
  const [savedOpen, setSavedOpen] = useState(false)
  // Pending action awaiting the discard confirm: { type: 'nav', id } | { type: 'close' }
  const [pendingAction, setPendingAction] = useState(null)
  const [search, setSearch] = useState('')
  const [activeKeys, setActiveKeys] = useState(() => new Set())
  // The pinned property id. Stays put through search/chip changes; navigation
  // moves it relative to its position in the current queue.
  const [currentId, setCurrentId] = useState(null)
  // Slide-over edit panel: closed by default (view-only map first), tabbed on Prescription.
  const [panelOpen, setPanelOpen] = useState(false)
  const [activeTab, setActiveTab] = useState('prescription')
  // Map FAB state. Layer toggles deliberately persist across Next/Prev (a
  // sweep with takeoffs hidden stays that way); zoomNonce re-centers the map.
  const [layers, setLayers] = useState(ALL_LAYERS_ON)
  const [zoomNonce, setZoomNonce] = useState(0)
  // Review flags: open "someone else should look at this" alerts, keyed by
  // aspire property id, plus the flag-capture dialog state.
  const [openFlags, setOpenFlags] = useState({})
  const [flagDialogOpen, setFlagDialogOpen] = useState(false)
  const [flagCategory, setFlagCategory] = useState(null)
  const [flagNotes, setFlagNotes] = useState('')
  const [flagSaving, setFlagSaving] = useState(false)
  const [flagSnackOpen, setFlagSnackOpen] = useState(false)
  // Last in-queue position — lets Next resume the sweep after the pinned
  // property drops out of the queue (e.g. it was just fixed and saved).
  const lastQueuePosRef = useRef(0)

  // Fresh triage state per open; pin the requested property (Setup Board row
  // click) or fall back to the first one. `properties` is intentionally not a
  // dep — reopening resets, background list changes don't.
  useEffect(() => {
    if (!open) return
    setSearch('')
    setActiveKeys(new Set())
    const requested = initialPropertyId != null
      ? properties.find(p => String(p.propertyId) === String(initialPropertyId))
      : null
    setCurrentId(requested ? requested.propertyId : (properties[0]?.propertyId ?? null))
    setDirty(false)
    setPendingAction(null)
    setPanelOpen(false)
    setActiveTab('prescription')
    setLayers(ALL_LAYERS_ON)
    setZoomNonce(0)
    setFlagDialogOpen(false)
    lastQueuePosRef.current = 0
  }, [open]) // properties omitted on purpose (see comment above)

  // Open review flags for the whole portfolio — cheap (open flags are few)
  // and refreshed per open so another reviewer's flags are current.
  useEffect(() => {
    if (!open) return undefined
    let cancelled = false
    axios.get(`${url}/api/hmi/property-setup/flags?status=open`)
      .then(res => { if (!cancelled) setOpenFlags(buildOpenFlagMap(res.data?.flags)) })
      .catch(() => { if (!cancelled) setOpenFlags({}) })
    return () => { cancelled = true }
  }, [open, url])

  const queue = useMemo(
    () => buildReviewQueue(properties, { search, activeKeys }),
    [properties, search, activeKeys]
  )
  const property = useMemo(
    () => properties.find(p => String(p.propertyId) === String(currentId)) || null,
    [properties, currentId]
  )
  const queuePos = useMemo(
    () => queue.findIndex(p => String(p.propertyId) === String(currentId)),
    [queue, currentId]
  )

  useEffect(() => {
    if (queuePos >= 0) lastQueuePosRef.current = queuePos
  }, [queuePos])

  const runAction = useCallback((action) => {
    setDirty(false)
    // Dismiss a lingering Saved snackbar — the fix→Next→fix sweep otherwise
    // leaves "Saved" hanging over the next property.
    setSavedOpen(false)
    if (action.type === 'reset') {
      // Reset keeps the panel open — remounting the form re-seeds it.
      setResetNonce(n => n + 1)
      return
    }
    // Navigation (or close) lands view-only — closing the panel here is a
    // no-op on close and correct on nav.
    setPanelOpen(false)
    if (action.type === 'close') onClose()
    else setCurrentId(action.id)
  }, [onClose])

  const requestAction = useCallback((action) => {
    if (dirty) setPendingAction(action)
    else runAction(action)
  }, [dirty, runAction])

  // Search is a "find this property" gesture: when the typed query filters the
  // pinned property out of the queue, jump to the first match instead of
  // leaving the old property on screen (which read as "search is broken").
  // Never jumps while the form is dirty — unsaved edits are not silently
  // dropped (the pin stays and the indicator shows "Filtered out"). Chip-only
  // filtering keeps the original pin-stays sweep behavior.
  useEffect(() => {
    if (!open || dirty || !search.trim()) return
    if (queuePos !== -1 || queue.length === 0) return
    runAction({ type: 'nav', id: queue[0].propertyId })
  }, [open, dirty, search, queue, queuePos, runAction])

  const toggleChip = (key) => {
    setActiveKeys(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  if (!open) return null

  const emptyQueue = queue.length === 0
  const pinnedOut = queuePos === -1 && !emptyQueue
  const prevTarget = queuePos > 0 ? queue[queuePos - 1] : null
  const nextTarget = queuePos >= 0
    ? (queuePos < queue.length - 1 ? queue[queuePos + 1] : null)
    // Resume the sweep at the position the user was at — NOT queue[0].
    : (pinnedOut ? queue[Math.min(lastQueuePosRef.current, queue.length - 1)] : null)
  const indicator = emptyQueue
    ? '0 match'
    : (pinnedOut ? `Filtered out · ${queue.length} match` : `${queuePos + 1} of ${queue.length}`)

  const issueCount = property ? TRIAGE_FILTERS.filter(f => f.test(property)).length : 0
  const acres = property?.siteTakeoff?.totalSF ? (property.siteTakeoff.totalSF / 43560).toFixed(1) : null
  // Routing snapshot for the header: route/crew label (getPropertyRoute is the
  // single source of truth), scheduled day(s), and the last/next visit dates.
  const routeLabel = property ? (getPropertyRoute(property) || '—') : '—'
  const dayLabel = property
    ? ((property.scheduledDays || []).map(d => String(d).slice(0, 3)).join(' · ') || '—')
    : '—'
  const lastVisitLabel = formatShortDate(property?.lastMaintenanceVisitDate)
  const nextVisitLabel = formatShortDate(property?.nextVisitDate)
  const takeoffHref = property?.jobsiteId ? `/site-takeoff/${property.jobsiteId}` : null
  const aspireHref = property ? `https://cloud.youraspire.com/app/properties/overview/${property.propertyId}` : null
  const subtitle = property
    ? [property.address1, acres ? `${acres} ac` : null, property.companyName].filter(Boolean).join(' · ')
    : ''

  const chipLinkStyle = { fontFamily: 'monospace', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', background: 'rgba(251,248,241,0.96)', border: `1px solid ${V4_T.line}`, borderRadius: 8, padding: '7px 12px', color: V4_T.ink2, textDecoration: 'none', boxShadow: '0 1px 6px rgba(28,36,25,0.07)' }
  const statChipStyle = { background: 'rgba(251,248,241,0.96)', border: `1px solid ${V4_T.line}`, borderRadius: 10, padding: '8px 12px', boxShadow: '0 1px 6px rgba(28,36,25,0.07)' }
  const statLabelStyle = { fontFamily: 'monospace', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: V4_T.muteSoft, textTransform: 'uppercase' }
  const infoValueStyle = { fontSize: 13, fontWeight: 700, color: V4_T.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 160 }
  // Map FABs: solid green while the layer is shown, cream while hidden.
  const fabOnStyle = { background: V4_T.green, color: '#fff' }
  const fabOffStyle = { background: 'rgba(251,248,241,0.96)', color: V4_T.muteSoft }
  const toggleLayer = (key) => setLayers(prev => ({ ...prev, [key]: !prev[key] }))

  // Review flag for the pinned property (open flags only).
  const currentFlag = property ? (openFlags[Number(property.propertyId)] || null) : null

  const submitFlag = async () => {
    if (!property || !flagCategory) return
    setFlagSaving(true)
    try {
      const res = await axios.post(`${url}/api/hmi/property-setup/flags`, {
        aspirePropertyId: Number(property.propertyId),
        propertyName: property.propertyName || property.name || '',
        category: flagCategory,
        notes: flagNotes.trim()
      })
      const flag = res.data?.flag
      if (flag) setOpenFlags(prev => ({ ...prev, [Number(property.propertyId)]: flag }))
      setFlagDialogOpen(false)
      setFlagSnackOpen(true)
    } catch (err) {
      console.error('Failed to flag property for review:', err)
    } finally {
      setFlagSaving(false)
    }
  }

  const resolveCurrentFlag = async () => {
    if (!currentFlag || !property) return
    try {
      await axios.patch(`${url}/api/hmi/property-setup/flags/${currentFlag._id}/resolve`)
      setOpenFlags(prev => {
        const next = { ...prev }
        delete next[Number(property.propertyId)]
        return next
      })
    } catch (err) {
      console.error('Failed to resolve review flag:', err)
    }
  }

  return (
    <Dialog fullScreen open={open} onClose={() => requestAction({ type: 'close' })} aria-labelledby="property-review-title">
      <div style={{ display: 'grid', gridTemplateRows: 'auto auto 1fr', height: '100vh', background: V4_T.surface }}>
        {/* Header band */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '12px 20px 10px', borderBottom: `1px solid ${V4_T.line}` }}>
          <IconButton aria-label="close review" onClick={() => requestAction({ type: 'close' })} size="small">
            <CloseIcon />
          </IconButton>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: 'monospace', fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', color: V4_T.mute }}>PROPERTY REVIEW</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
              <h1 id="property-review-title" style={{ margin: 0, fontFamily: SERIF, fontSize: 26, fontWeight: 700, color: V4_T.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {property ? (property.propertyName || property.name) : 'No properties match'}
              </h1>
              {issueCount > 0 && (
                <span style={{ fontFamily: 'monospace', fontSize: 11, fontWeight: 700, color: V4_T.rust, background: V4_T.rustSoft, border: `1px solid ${V4_T.rust}33`, borderRadius: 999, padding: '3px 10px', whiteSpace: 'nowrap' }}>
                  {`${issueCount} issues`}
                </span>
              )}
            </div>
            {subtitle && <div style={{ fontSize: 12.5, color: V4_T.mute, marginTop: 2 }}>{subtitle}</div>}
          </div>
          {property && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 20, padding: '0 16px', borderLeft: `1px solid ${V4_T.line}`, borderRight: `1px solid ${V4_T.line}` }}>
              <div>
                <div style={statLabelStyle}>Route</div>
                <div style={infoValueStyle} title={routeLabel}>{routeLabel}</div>
              </div>
              <div>
                <div style={statLabelStyle}>Day</div>
                <div style={infoValueStyle}>{dayLabel}</div>
              </div>
              <div>
                <div style={statLabelStyle}>Last visit</div>
                <div style={infoValueStyle}>{lastVisitLabel}</div>
              </div>
              <div>
                <div style={statLabelStyle}>Next visit</div>
                <div style={infoValueStyle}>{nextVisitLabel}</div>
              </div>
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {takeoffHref && (
              <Button variant="outlined" component="a" href={takeoffHref} target="_blank" rel="noopener noreferrer" aria-label="open takeoff">
                Open takeoff
              </Button>
            )}
            {aspireHref && (
              <IconButton size="small" component="a" href={aspireHref} target="_blank" rel="noopener noreferrer" aria-label="open in Aspire" title="Open in Aspire">
                <LaunchIcon fontSize="small" />
              </IconButton>
            )}
            {property && (
              <Button variant="contained" onClick={() => setPanelOpen(true)} style={{ background: V4_T.green, color: '#fff', fontWeight: 700 }} aria-label="edit property">
                Edit property
                {dirty && !panelOpen && <span aria-hidden="true" style={{ marginLeft: 8, width: 8, height: 8, borderRadius: '50%', background: V4_T.rust, display: 'inline-block' }} />}
              </Button>
            )}
          </div>
        </div>

        {/* Triage strip — functionally identical to the old top bar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 20px', borderBottom: `1px solid ${V4_T.line}`, flexWrap: 'wrap', background: V4_T.surface2 }}>
          <input
            type="text"
            aria-label="Search properties"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, client, community…"
            style={{ width: 260, padding: '6px 10px', border: `1px solid ${V4_T.line}`, borderRadius: 6, fontSize: 13 }}
          />
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            {TRIAGE_FILTERS.map(({ key, label }) => {
              const selected = activeKeys.has(key)
              return (
                <Chip
                  key={key}
                  label={label}
                  size="small"
                  clickable
                  onClick={() => toggleChip(key)}
                  variant={selected ? 'default' : 'outlined'}
                  aria-pressed={selected}
                  style={selected ? { background: V4_T.green, color: '#fff' } : undefined}
                />
              )
            })}
          </div>
          <span style={{ fontSize: 12, color: V4_T.muteSoft, marginLeft: 'auto', whiteSpace: 'nowrap' }}>
            {`${queue.length} match`}
          </span>
        </div>

        {/* Map region with floating overlays + slide-over panel */}
        {/* overflow:hidden — the off-canvas panel (translateX(100%)) otherwise
            adds 440px of horizontal scrollable overflow to the dialog. */}
        <div style={{ position: 'relative', minHeight: 0, overflow: 'hidden' }}>
          <div style={{ position: 'absolute', inset: 0 }}>
            <PropertyReviewMap
              key={property?.propertyId ?? 'none'}
              url={url}
              jobsiteId={property?.jobsiteId || null}
              showGeofence={layers.geofence}
              showParcels={layers.parcels}
              showTakeoffs={layers.takeoffs}
              zoomNonce={zoomNonce}
            />
          </div>

          {property && (
            <div style={{ position: 'absolute', top: 16, left: 16, zIndex: 1000, display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 320 }}>
              <PropertyReviewStatusCard
                property={property}
                jobsiteId={property.jobsiteId || null}
                onOpenChecklist={() => { setActiveTab('checklist'); setPanelOpen(true) }}
              />
              {currentFlag && (
                <div style={{ background: 'rgba(251,248,241,0.97)', border: `1.5px solid ${V4_T.rust}`, borderRadius: 12, padding: '12px 14px', boxShadow: '0 2px 10px rgba(28,36,25,0.1)' }}>
                  <div style={{ fontFamily: 'monospace', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.08em', color: V4_T.rust, textTransform: 'uppercase' }}>
                    {`Flagged · ${flagCategoryLabel(currentFlag.category)}`}
                  </div>
                  {currentFlag.notes && (
                    <div style={{ fontSize: 13, color: V4_T.ink, margin: '6px 0 2px', lineHeight: 1.4 }}>{currentFlag.notes}</div>
                  )}
                  <div style={{ fontSize: 11, color: V4_T.mute, marginTop: 4 }}>
                    {[currentFlag.raisedBy?.name, currentFlag.createdAt ? formatShortDate(currentFlag.createdAt) : null].filter(Boolean).join(' · ')}
                  </div>
                  <Button size="small" variant="outlined" aria-label="resolve flag" onClick={resolveCurrentFlag} style={{ marginTop: 8, color: V4_T.rust, borderColor: V4_T.rust }}>
                    Resolve
                  </Button>
                </div>
              )}
            </div>
          )}

          {property && (
            <div style={{ position: 'absolute', top: 16, right: panelOpen ? 456 : 16, zIndex: 1000, display: 'flex', gap: 8, transition: 'right .2s' }}>
              {!currentFlag && (
                <button
                  type="button"
                  aria-label="flag for review"
                  onClick={() => { setFlagCategory(null); setFlagNotes(''); setFlagDialogOpen(true) }}
                  style={{ ...chipLinkStyle, color: V4_T.rust, borderColor: V4_T.rust, cursor: 'pointer' }}
                >
                  Flag for review
                </button>
              )}
              {/* Edit geofence / Mark herbicide areas / Set pin removed —
                  they deep-linked to the same takeoff page as the header's
                  "Open takeoff" button (Set pin duplicated "Open in Aspire").
                  In-place map editing is a future project; until then the
                  header actions are the single entry points. */}
            </div>
          )}

          {property && (
            <div style={{ position: 'absolute', bottom: 16, left: 16, zIndex: 1000, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <div style={statChipStyle}><div style={statLabelStyle}>Parcel</div><div style={{ fontSize: 13, fontWeight: 700, color: V4_T.ink }}>{acres ? `${acres} ac` : '—'}</div></div>
              <div style={statChipStyle}><div style={statLabelStyle}>Geofence</div><div style={{ fontSize: 13, fontWeight: 700, color: V4_T.ink }}>{property.setupChecklist?.geofenceSet?.confirmed ? 'Set' : '—'}</div></div>
              <div style={statChipStyle}><div style={statLabelStyle}>Herbicide</div><div style={{ fontSize: 13, fontWeight: 700, color: V4_T.ink }}>{property.siteTakeoff?.hasHerbicideAreas ? 'Marked' : 'None'}</div></div>
              <div style={statChipStyle}><div style={statLabelStyle}>Lat / Lng</div><div style={{ fontSize: 13, fontWeight: 700, color: V4_T.ink, fontFamily: 'monospace' }}>{(property.latitude && property.longitude) ? `${Number(property.latitude).toFixed(4)} · ${Number(property.longitude).toFixed(4)}` : '—'}</div></div>
            </div>
          )}

          {/* Map FAB stack: zoom-to-property plus layer toggles. Only rendered
              when the map has a jobsite to show; shifts left with the panel
              like the top-right action chips. */}
          {property?.jobsiteId && (
            <div style={{ position: 'absolute', bottom: 16, right: panelOpen ? 456 : 16, zIndex: 1000, display: 'flex', flexDirection: 'column', gap: 10, transition: 'right .2s' }}>
              <Fab size="small" aria-label="zoom to property" title="Zoom to property" style={fabOffStyle} onClick={() => setZoomNonce(n => n + 1)}>
                <MyLocationIcon fontSize="small" />
              </Fab>
              <Fab size="small" aria-label="toggle geofence" title={layers.geofence ? 'Hide geofence' : 'Show geofence'} aria-pressed={layers.geofence} style={layers.geofence ? fabOnStyle : fabOffStyle} onClick={() => toggleLayer('geofence')}>
                <TrackChangesIcon fontSize="small" />
              </Fab>
              <Fab size="small" aria-label="toggle parcel outlines" title={layers.parcels ? 'Hide parcel outlines' : 'Show parcel outlines'} aria-pressed={layers.parcels} style={layers.parcels ? fabOnStyle : fabOffStyle} onClick={() => toggleLayer('parcels')}>
                <BorderOuterIcon fontSize="small" />
              </Fab>
              <Fab size="small" aria-label="toggle takeoff areas" title={layers.takeoffs ? 'Hide takeoff areas' : 'Show takeoff areas'} aria-pressed={layers.takeoffs} style={layers.takeoffs ? fabOnStyle : fabOffStyle} onClick={() => toggleLayer('takeoffs')}>
                <LayersIcon fontSize="small" />
              </Fab>
            </div>
          )}

          <div style={{ position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 1000, display: 'flex', alignItems: 'center', gap: 14, background: 'rgba(251,248,241,0.96)', border: `1px solid ${V4_T.line}`, borderRadius: 999, padding: '8px 14px', boxShadow: '0 2px 10px rgba(28,36,25,0.1)' }}>
            <Button variant="outlined" size="small" disabled={!prevTarget} onClick={() => requestAction({ type: 'nav', id: prevTarget.propertyId })}>
              ‹ Previous
            </Button>
            <span style={{ fontSize: 13, color: V4_T.ink2, minWidth: 110, textAlign: 'center' }}>{indicator}</span>
            <Button variant="outlined" size="small" disabled={!nextTarget} onClick={() => requestAction({ type: 'nav', id: nextTarget.propertyId })}>
              Next ›
            </Button>
          </div>

          {/* Slide-over edit panel — ALWAYS rendered so the single form
              instance (and its edits/dirty fingerprint) survives open/close. */}
          <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: 440, background: '#fff', borderLeft: `1px solid ${V4_T.line}`, boxShadow: '-6px 0 18px rgba(28,36,25,0.10)', zIndex: 1050, display: 'flex', flexDirection: 'column', transform: panelOpen ? 'none' : 'translateX(100%)', visibility: panelOpen ? 'visible' : 'hidden', transition: panelOpen ? 'transform .2s ease' : 'transform .2s ease, visibility 0s linear .2s' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: `1px solid ${V4_T.line}` }}>
              <span style={{ fontFamily: SERIF, fontSize: 16, fontWeight: 700, color: V4_T.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {property ? (property.propertyName || property.name) : ''}
                {/* Unsaved edits may live on a non-visible tab — surface dirty
                    here so it's discoverable with the panel open. */}
                {dirty && <span aria-label="unsaved changes" style={{ marginLeft: 8, width: 8, height: 8, borderRadius: '50%', background: V4_T.rust, display: 'inline-block' }} />}
              </span>
              <IconButton aria-label="close edit panel" size="small" onClick={() => setPanelOpen(false)}>
                <ChevronRightIcon />
              </IconButton>
            </div>
            <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="fullWidth" indicatorColor="primary" textColor="primary" style={{ borderBottom: `1px solid ${V4_T.line}`, minHeight: 40 }}>
              <Tab label="Prescription" value="prescription" style={{ minHeight: 40, fontSize: 12 }} />
              <Tab label="Schedule" value="schedule" style={{ minHeight: 40, fontSize: 12 }} />
              <Tab label="Checklist" value="checklist" style={{ minHeight: 40, fontSize: 12 }} />
              <Tab label="Billing" value="billing" style={{ minHeight: 40, fontSize: 12 }} />
            </Tabs>
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {property && (
                <PropertyEditForm
                  key={resetNonce}
                  property={property}
                  crews={crews}
                  url={url}
                  propertyOptions={propertyOptions}
                  visibleSections={TAB_SECTIONS[activeTab]}
                  hideTitle
                  onSave={(payload, opts) => onSave(property.propertyId, payload, opts)}
                  onClose={() => requestAction({ type: 'close' })}
                  onSaved={() => { setDirty(false); setSavedOpen(true) }}
                  onCancel={() => requestAction({ type: 'reset' })}
                  cancelLabel="Reset"
                  onDirtyChange={setDirty}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <Dialog open={!!pendingAction} onClose={() => setPendingAction(null)} maxWidth="xs">
        <DialogTitle>Discard unsaved changes?</DialogTitle>
        <DialogActions>
          <Button onClick={() => setPendingAction(null)}>Stay</Button>
          <Button
            style={{ color: '#a9341d', fontWeight: 600 }}
            onClick={() => {
              const action = pendingAction
              setPendingAction(null)
              if (action) runAction(action)
            }}
          >
            Discard
          </Button>
        </DialogActions>
      </Dialog>

      {/* Flag-for-review capture: category + free-form note for the next reviewer. */}
      <Dialog open={flagDialogOpen} onClose={() => setFlagDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Flag for review</DialogTitle>
        <DialogContent>
          <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
            {FLAG_CATEGORIES.map(({ value, label }) => {
              const selected = flagCategory === value
              return (
                <button
                  key={value}
                  type="button"
                  onClick={() => setFlagCategory(value)}
                  aria-pressed={selected}
                  style={{
                    border: `1.5px solid ${selected ? V4_T.rust : V4_T.line}`,
                    background: selected ? V4_T.rust : '#fff',
                    color: selected ? '#fff' : V4_T.ink2,
                    borderRadius: 999,
                    padding: '6px 14px',
                    fontSize: 12.5,
                    fontWeight: 700,
                    cursor: 'pointer'
                  }}
                >
                  {label}
                </button>
              )
            })}
          </div>
          <TextField
            fullWidth
            multiline
            minRows={3}
            size="small"
            variant="outlined"
            value={flagNotes}
            onChange={(e) => setFlagNotes(e.target.value)}
            placeholder="What should the next person look at?"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFlagDialogOpen(false)} disabled={flagSaving}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!flagCategory || flagSaving}
            onClick={submitFlag}
            style={{ fontWeight: 600, ...(flagCategory && !flagSaving ? { background: V4_T.rust, color: '#fff' } : {}) }}
          >
            {flagSaving ? 'Flagging…' : 'Flag property'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={savedOpen}
        autoHideDuration={2500}
        onClose={() => setSavedOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message="Saved"
      />
      <Snackbar
        open={flagSnackOpen}
        autoHideDuration={2500}
        onClose={() => setFlagSnackOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message="Flagged for review"
      />
    </Dialog>
  )
}

export default PropertyReviewModal
